import { useContext, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { HeadlessRoute, HeadlessRoutes } from '@/constants';
import { addProductFromPage as addProductFromPageToShoppingList } from '@/hooks/dom/useOpenPDP';
import { addProductsFromCartToQuote, addProductsToDraftQuote, getCartProducts } from '@/hooks/dom/utils';
import { setElementsListenersConfig } from '@/lib/config';
import { useB3Lang } from '@/lib/lang';
import { addProductsToShoppingList } from '@/pages/PDP/addProductsToShoppingList';
import { addProductsToShoppingListErrorHandler } from '@/pages/PDP/addProductsToShoppingListErrorHandler';
import { useAddedToShoppingListAlert } from '@/pages/PDP/useAddedToShoppingListAlert';
import { type SetOpenPage } from '@/pages/SetOpenPage';
import { ShoppingListsItemsProps } from '@/pages/ShoppingLists/config';
import { CustomStyleContext } from '@/shared/customStyleButton';
import { GlobalContext } from '@/shared/global';
import { getAllowedRoutesWithoutComponent } from '@/shared/routeList';
import { getB2BShoppingList, getBcShoppingList, superAdminCompanies } from '@/shared/service/b2b';
import B3Request from '@/shared/service/request/b3Fetch';
import {
  formattedQuoteDraftListSelector,
  isB2BUserSelector,
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from '@/store';
import { setB2BToken } from '@/store/slices/company';
import { QuoteItem } from '@/types/quotes';
import CallbackManager from '@/utils/b3CallbackManager';
import b2bLogger from '@/utils/b3Logger';
import { logoutSession } from '@/utils/b3logout';
import { LineItem } from '@/utils/b3Product/b3Product';
import createShoppingList from '@/utils/b3ShoppingList/b3ShoppingList';
import b3TriggerCartNumber from '@/utils/b3TriggerCartNumber';
import { channelId } from '@/utils/basicConfig';
import { getCurrentCustomerInfo } from '@/utils/loginInfo';
import { endMasquerade, startMasquerade } from '@/utils/masquerade';
import { CustomerRole } from '@/types';
import { getSku } from './getSku';
import { getCart } from '@/shared/service/bc/graphql/cart';
import { globalSnackbar } from '@/utils/b3Tip';

interface HeadlessControllerProps {
  setOpenPage: SetOpenPage;
}

interface ProductOptionsProps {
  name: string;
  nameId: number;
  value: number | string;
  valueId: number;
}

interface DiscountsProps {
  discountedAmount: number;
  id: string;
}

interface CustomItemProps {
  extendedListPrice: number;
  id: string;
  listPrice: number;
  name: string;
  quantity: number;
  sku: string;
}
interface DigitalItemProps extends CustomItemProps {
  options: ProductOptionsProps[];
  brand: string;
  couponAmount: number;
  discountAmount: number;
  discounts: DiscountsProps[];
  extendedSalePrice: number;
  imageUrl: string;
  isTaxable: boolean;
  originalPrice: number;
  parentId?: string;
  productId: number;
  salePrice: number;
  url: string;
  variantId: number;
}

interface CustomItemProps {
  extendedListPrice: number;
  id: string;
  listPrice: number;
  name: string;
  quantity: number;
  sku: string;
}
interface LineItemsProps {
  customItems: CustomItemProps[];
  digitalItems: DigitalItemProps[];
  physicalItems: PhysicalItemProps[];
}
interface CartInfoProps {
  baseAmount: number;
  cartAmount: number;
  coupons: any;
  createdTime: string;
  currency: {
    code: string;
    decimalPlaces: number;
    name: string;
    symbol: string;
  };
  customerId: number;
  discountAmount: number;
  email: string;
  id: string;
  isTaxIncluded: boolean;
  lineItems: LineItemsProps;
  locale: string;
  updatedTime: string;
}

interface ItemProps {
  sku: string;
  quantity: number;
  productId: number;
  optionSelections: Array<{
    entityId: number;
    name: string;
    value: string;
    valueEntityId: number;
  }>;
  [key: string]: any; // Allow other properties
}
interface PhysicalItemProps extends DigitalItemProps {
  giftWrapping: {
    amount: number;
    message: string;
    name: string;
  };
  isShippingRequire: boolean;
  parentEntityId?: string | null;
}

export interface FormattedQuoteItem
  extends Omit<QuoteItem['node'], 'optionList' | 'calculatedValue' | 'productsSearch'> {
  optionSelections: {
    optionId: string | number;
    optionValue: number;
  }[];
}

interface HeadlessControllerProps {
  setOpenPage: SetOpenPage;
}

export type ProductMappedAttributes = ReturnType<typeof transformOptionSelectionsToAttributes>;

const transformOptionSelectionsToAttributes = (items: LineItem[]) =>
  items.map((product) => {
    const selectedOptions =
      product.selectedOptions?.reduce(
        (accumulator: Record<string, number>, { optionEntityId, optionValueEntityId }) => {
          accumulator[`attribute[${optionEntityId}]`] = optionValueEntityId;

          return accumulator;
        },
        {},
      ) ?? {};

    return {
      ...product,
      productId: product.productEntityId,
      selectedOptions,
      optionSelections: selectedOptions,
    };
  });

const Manager = new CallbackManager();

export default function HeadlessController({ setOpenPage }: HeadlessControllerProps) {
  const storeDispatch = useAppDispatch();
  const store = useAppStore();
  const b3Lang = useB3Lang();

  const { state: globalState } = useContext(GlobalContext);
  const isB2BUser = useAppSelector(isB2BUserSelector);
  const salesRepCompanyId = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.id);
  const customer = useAppSelector(({ company }) => company.customer);
  const role = useAppSelector(({ company }) => company.customer.role);
  const productList = useAppSelector(formattedQuoteDraftListSelector);
  const isAgenting = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting);
  const B2BToken = useAppSelector(({ company }) => company.tokens.B2BToken);


  const displayAddedToShoppingListAlert = useAddedToShoppingListAlert();

  const {
    state: { addQuoteBtn, shoppingListBtn, addToAllQuoteBtn },
  } = useContext(CustomStyleContext);
 const { addToQuoteFromCart, addToQuoteFromCookie } = addProductsFromCartToQuote(
    setOpenPage,
    b3Lang,
  );

 

  const {
    registerEnabled,
    productQuoteEnabled,
    cartQuoteEnabled,
    shoppingListEnabled,
    quoteConfig,
  } = globalState;

  const saveFn = () => {
    setOpenPage({
      isOpen: true,
      openUrl: '/register',
    });
  };

  const gotoShoppingDetail = (id: number | string) => {
    setOpenPage({
      isOpen: true,
      openUrl: `/shoppingList/${id}`,
      params: {
        shoppingListBtn: 'add',
      },
    });
  };

  const customerId = customer.id;
  // Keep updated values
  const salesRepCompanyIdRef = useRef(Number(salesRepCompanyId));
  const customerIdRef = useRef(customerId);
  const customerRef = useRef(customer);
  const roleRef = useRef(Number(role));
  const isB2BUserRef = useRef(isB2BUser);
  const productQuoteEnabledRef = useRef(productQuoteEnabled);
  const shoppingListEnabledRef = useRef(shoppingListEnabled);
  const cartQuoteEnabledRef = useRef(cartQuoteEnabled);
  const addQuoteBtnRef = useRef(addQuoteBtn);
  const shoppingListBtnRef = useRef(shoppingListBtn);
  const addToAllQuoteBtnRef = useRef(addToAllQuoteBtn);

  salesRepCompanyIdRef.current = Number(salesRepCompanyId);
  customerIdRef.current = customerId;
  customerRef.current = customer;
  roleRef.current = Number(role);
  isB2BUserRef.current = isB2BUser;
  productQuoteEnabledRef.current = productQuoteEnabled;
  shoppingListEnabledRef.current = shoppingListEnabled;
  cartQuoteEnabledRef.current = cartQuoteEnabled;
  addQuoteBtnRef.current = addQuoteBtn;
  shoppingListBtnRef.current = shoppingListBtn;
  addToAllQuoteBtnRef.current = addToAllQuoteBtn;
  

  useEffect(() => {
    window.b2b = {
      ...window.b2b,
      callbacks: Manager,
      utils: {
        getRoutes: () => getAllowedRoutesWithoutComponent(globalState),
        openPage: (page: HeadlessRoute) =>
          setTimeout(() => {
            if (page === 'CLOSE') {
              setOpenPage({ isOpen: false });
              return;
            }
            const openUrl = page.startsWith('/') ? page : HeadlessRoutes[page];
            setOpenPage({ isOpen: true, openUrl });
          }, 0),
        setConfig: (key: string, value: string) => {
          setElementsListenersConfig(key, value);
        },
        quote: {
          addProductFromPage: async (item) => {
            const productWithSku = {
              ...item,
              sku: await getSku(item),
            };

            return addProductsToDraftQuote([productWithSku], setOpenPage, b3Lang);
          },
          addProductsFromCart: addToQuoteFromCookie,
          addProductsFromCartId: addToQuoteFromCart,
          addProducts: async (items: LineItem[]) => {
            const products = await Promise.all(
              items.map(async (item) => ({
                ...item,
                sku: await getSku(item),
              })),
            );

            return addProductsToDraftQuote(products, setOpenPage, b3Lang);
          },
          getQuoteConfigs: () => quoteConfig,
          getCurrent: () => ({ productList }),
          getButtonInfo: () => ({
            ...addQuoteBtnRef.current,
            enabled: productQuoteEnabledRef.current,
          }),
          getButtonInfoAddAllFromCartToQuote: () => ({
            ...addToAllQuoteBtnRef.current,
            enabled: cartQuoteEnabledRef.current,
          }),
        },
        user: {
          getProfile: () => ({ ...customerRef.current, role }),
          getMasqueradeState: async () => {
            if (typeof customerRef.current.b2bId !== 'number') {
              return {
                current_company_id: salesRepCompanyIdRef.current,
                companies: [],
              };
            }
            // get companies list
            const {
              superAdminCompanies: { edges: companies = [] },
            } = await superAdminCompanies(customerRef.current.b2bId, {
              first: 50,
              offset: 0,
              orderBy: 'companyName',
            });

            return {
              current_company_id: salesRepCompanyIdRef.current,
              companies: companies.map(({ node }: { node: CustomFieldStringItems }) => node),
            };
          },
          getB2BToken: () => B2BToken,
          setMasqueradeCompany: (companyId) => {
            if (typeof customerRef.current.b2bId !== 'number') return;
            startMasquerade(
              {
                companyId,
                customerId: customerIdRef.current,
              },
              store,
            );
          },
          endMasquerade: () => {
            if (typeof customerRef.current.b2bId !== 'number') return;
            endMasquerade(store);
          },
          graphqlBCProxy: B3Request.graphqlBCProxy,
          loginWithB2BStorefrontToken: async (token: string) => {
            storeDispatch(setB2BToken(token));
            await getCurrentCustomerInfo(token);
          },
          logout: async () => {
            try {
              if (isAgenting) {
                await endMasquerade(store);
              }
            } catch (e) {
              b2bLogger.error(e);
            } finally {
              window.sessionStorage.clear();
              logoutSession();
              window.b2b.callbacks.dispatchEvent('on-logout');
            }
          },
        },
        shoppingList: {
          itemFromCurrentPage: window.b2b?.utils?.shoppingList?.itemFromCurrentPage ?? [],
          addProductFromPage: (item) => {
            window.b2b.utils.shoppingList.itemFromCurrentPage =
              transformOptionSelectionsToAttributes([item]);
            addProductFromPageToShoppingList({
              role: roleRef.current,
              storeDispatch,
              saveFn,
              setOpenPage,
              registerEnabled,
            });
          },
          addProducts: (shoppingListId, items) =>
            addProductsToShoppingList({
              shoppingListId,
              items: transformOptionSelectionsToAttributes(items),
              isB2BUser: isB2BUserRef.current,
              customerGroupId: customerRef.current.customerGroupId,
            })
              .then(() => displayAddedToShoppingListAlert(shoppingListId.toString()))
              .catch(addProductsToShoppingListErrorHandler),
          createNewShoppingList: async (name, description) => {
            const { shoppingListsCreate } = await createShoppingList({
              data: { name, description },
              isB2BUser: isB2BUserRef.current,
            });

            return shoppingListsCreate.shoppingList;
          },
          shoppingListFromCart: async (name: string) => {
            const isLogin = role !== CustomerRole.GUEST;
            if (!isLogin) {
              window.location.reload();
            }
            try {
              const description = name;
              const { shoppingListsCreate, customerShoppingListsCreate } = await createShoppingList(
                {
                  data: { name, description },
                  isB2BUser: isB2BUserRef.current,
                },
              );
  
              const shoppingListId = shoppingListsCreate
                ? shoppingListsCreate.shoppingList.id
                : customerShoppingListsCreate.shoppingList.id;
              const cartInfoWithOptions: CartInfoProps | any = await getCart();
  
              if (!cartInfoWithOptions.data.site.cart) {
                globalSnackbar.error('No products in Cart.');
                return;
              }
              const { lineItems } = cartInfoWithOptions.data.site.cart;
              const { cartProductsList, noSkuProducts } = getCartProducts(lineItems);
              if (noSkuProducts.length > 0) {
                globalSnackbar.error('Can not add products without SKU.');
              }
              if (cartProductsList.length === 0) {
                globalSnackbar.error('No products being added.');
              }
              if (noSkuProducts.length === cartProductsList.length) return;
              const newCartProductsList = cartProductsList.filter(
                (product: PhysicalItemProps) => !product.parentEntityId,
              );
  
              await Promise.all(
                newCartProductsList.map(async (product: ItemProps) => {
                  // Transform cart options from entityId/valueEntityId to optionEntityId/optionValueEntityId
                  const transformedSelectedOptions = product.selectedOptions?.map((option: any) => ({
                    ...option,
                    optionEntityId: option.optionEntityId ?? option.entityId,
                    optionValueEntityId: option.optionValueEntityId ?? option.valueEntityId,
                  })) ?? [];

                  const transformedOptionSelections = transformedSelectedOptions.reduce((acc: Record<string, number>, option: any) => {
                    acc[`attribute[${option.optionEntityId}]`] = option.optionValueEntityId;
                    return acc;
                  }, {});
                  const item = {
                    sku: product.sku,
                    quantity: product.quantity,
                    productId: product.productEntityId,
                    selectedOptions: transformedSelectedOptions,
                    optionSelections: transformedOptionSelections,
                    productEntityId: product.productEntityId,
                  };
                  
                  await addProductsToShoppingList({
                    shoppingListId,
                    items: transformOptionSelectionsToAttributes([item]),
                    isB2BUser: isB2BUserRef.current,
                    customerGroupId: customerRef.current.customerGroupId,
                    success: false,
                  });
                }),
              );
              globalSnackbar.success('Products were added to your shopping list', {
                action: {
                  label: 'View',
                  onClick: () => gotoShoppingDetail(shoppingListId),
                },
              });
            } catch (e) {
              globalSnackbar.error('Error adding products to shopping list');
              b2bLogger.error(e);
              return;
            }
          },
          getButtonInfo: () => ({
            ...shoppingListBtnRef.current,
            enabled: shoppingListEnabledRef.current,
          }),
          getLists: async () => {
            const { edges: list = [] } = isB2BUser
              ? await getB2BShoppingList()
              : await getBcShoppingList({ channelId });

            return list.map(({ node }: { node: ShoppingListsItemsProps }) => node);
          }
      },
      cart: {
        setEntityId: (entityId) => {
          Cookies.set('cartId', entityId);
          b3TriggerCartNumber();
        },
        getEntityId: () => Cookies.get('cartId'),
      },
    },
    };
    // disabling because we don't want to run this effect on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList, B2BToken, globalState, quoteConfig]);

  return null;
}